import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const AcceptInvitePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams?.get('token') || '';

  const [inviteData, setInviteData] = useState(null);
  const [orgName, setOrgName] = useState('');
  const [tokenError, setTokenError] = useState('');
  const [tokenLoading, setTokenLoading] = useState(true);

  const [formData, setFormData] = useState({ fullName: '', email: '', password: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenError('Invalid invite link. No token found.');
      setTokenLoading(false);
      return;
    }
    validateToken();
  }, [token]);

  const validateToken = async () => {
    setTokenLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('invite_links')
        ?.select('*, organizations(name)')
        ?.eq('token', token)
        ?.single();

      if (error || !data) {
        setTokenError('This invite link is invalid or has expired.');
        setTokenLoading(false);
        return;
      }

      if (data?.used_at) {
        setTokenError('This invite link has already been used.');
        setTokenLoading(false);
        return;
      }

      if (new Date(data?.expires_at) < new Date()) {
        setTokenError('This invite link has expired. Please ask your admin for a new one.');
        setTokenLoading(false);
        return;
      }

      setInviteData(data);
      setOrgName(data?.organizations?.name || 'your team');
      if (data?.email) {
        setFormData(prev => ({ ...prev, email: data?.email }));
      }
    } catch (err) {
      setTokenError('Failed to validate invite link.');
    } finally {
      setTokenLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    setGeneralError('');
  };

  const validate = () => {
    const newErrors = {};
    if (!formData?.fullName?.trim()) newErrors.fullName = 'Your name is required';
    if (!formData?.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/?.test(formData?.email)) newErrors.email = 'Invalid email address';
    if (!formData?.password) newErrors.password = 'Password is required';
    else if (formData?.password?.length < 8) newErrors.password = 'Password must be at least 8 characters';
    if (formData?.password !== formData?.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    setGeneralError('');

    try {
      // 1. Create the auth user
      const { data: authData, error: signUpError } = await supabase?.auth?.signUp({
        email: formData?.email,
        password: formData?.password,
        options: {
          data: {
            full_name: formData?.fullName,
            org_id: inviteData?.org_id,
            role: inviteData?.role || 'member',
          },
        },
      });

      if (signUpError) {
        setGeneralError(signUpError?.message || 'Failed to create account.');
        setIsLoading(false);
        return;
      }

      const userId = authData?.user?.id;

      if (userId) {
        // 2. Upsert profile with org_id and role
        await supabase?.from('profiles')?.upsert({
          id: userId,
          email: formData?.email,
          full_name: formData?.fullName,
          org_id: inviteData?.org_id,
          role: inviteData?.role || 'member',
        }, { onConflict: 'id' });

        // 3. Mark invite link as used
        await supabase?.from('invite_links')?.update({
          used_at: new Date()?.toISOString(),
          used_by: userId,
        })?.eq('token', token);
      }

      setSuccess(true);
      setTimeout(() => navigate('/dashboard'), 2500);
    } catch (err) {
      setGeneralError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Accept Invitation - Wilcart CRM</title>
        <meta name="description" content="Accept your team invitation and create your Wilcart CRM account." />
      </Helmet>
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 pointer-events-none" />
        <div className="absolute top-10 left-10 w-32 h-32 bg-primary/10 rounded-full blur-2xl pointer-events-none" />
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-secondary/10 rounded-full blur-2xl pointer-events-none" />

        <div className="relative w-full max-w-md">
          <div className="bg-card border border-border rounded-2xl shadow-elevation-2 p-6 sm:p-8">

            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-6">
                <img
                  src="/assets/images/wilcart_logo_1-1775846328647.png"
                  alt="Wilcart CRM Logo"
                  className="h-14 w-auto object-contain"
                />
                <span className="ml-3 text-2xl font-bold text-foreground">Wilcart</span>
              </div>
              <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-3">
                <Icon name="Link" size={14} />
                <span>Team Invitation</span>
              </div>
              <h2 className="text-xl font-semibold text-foreground">Join your team</h2>
              {!tokenLoading && !tokenError && orgName && (
                <p className="text-muted-foreground mt-1 text-sm">
                  You've been invited to join <span className="font-medium text-foreground">{orgName}</span>
                </p>
              )}
            </div>

            {/* Token loading */}
            {tokenLoading && (
              <div className="flex flex-col items-center py-8 space-y-3">
                <div className="w-8 h-8 border-2 border-border border-t-primary rounded-full animate-spin" />
                <p className="text-sm text-muted-foreground">Validating invite link…</p>
              </div>
            )}

            {/* Token error */}
            {!tokenLoading && tokenError && (
              <div className="flex flex-col items-center py-6 space-y-4">
                <div className="w-14 h-14 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <Icon name="XCircle" size={28} className="text-red-500" />
                </div>
                <p className="text-center text-sm text-red-600 dark:text-red-400">{tokenError}</p>
                <Button variant="outline" onClick={() => navigate('/login')}>
                  Go to Login
                </Button>
              </div>
            )}

            {/* Success */}
            {success && (
              <div className="flex flex-col items-center py-6 space-y-4">
                <div className="w-14 h-14 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                  <Icon name="CheckCircle" size={28} className="text-green-500" />
                </div>
                <p className="text-center font-medium text-foreground">Account created successfully!</p>
                <p className="text-center text-sm text-muted-foreground">Redirecting you to the dashboard…</p>
              </div>
            )}

            {/* Registration form */}
            {!tokenLoading && !tokenError && !success && inviteData && (
              <>
                {generalError && (
                  <div className="mb-5 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                    <div className="flex items-center space-x-2">
                      <Icon name="AlertCircle" size={16} className="text-red-500 flex-shrink-0" />
                      <p className="text-sm text-red-700 dark:text-red-400">{generalError}</p>
                    </div>
                  </div>
                )}

                {/* Role badge */}
                <div className="mb-5 p-3 bg-muted rounded-lg flex items-center space-x-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Shield" size={16} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">You'll be joining as</p>
                    <p className="text-sm font-medium text-foreground capitalize">{inviteData?.role || 'member'}</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    label="Your Full Name"
                    type="text"
                    name="fullName"
                    placeholder="e.g. John Smith"
                    value={formData?.fullName}
                    onChange={handleChange}
                    error={errors?.fullName}
                    required
                  />
                  <Input
                    label="Email Address"
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    value={formData?.email}
                    onChange={handleChange}
                    error={errors?.email}
                    required
                    disabled={!!inviteData?.email}
                  />
                  <Input
                    label="Password"
                    type="password"
                    name="password"
                    placeholder="At least 8 characters"
                    value={formData?.password}
                    onChange={handleChange}
                    error={errors?.password}
                    required
                  />
                  <Input
                    label="Confirm Password"
                    type="password"
                    name="confirmPassword"
                    placeholder="Repeat your password"
                    value={formData?.confirmPassword}
                    onChange={handleChange}
                    error={errors?.confirmPassword}
                    required
                  />

                  <Button
                    type="submit"
                    variant="default"
                    size="lg"
                    fullWidth
                    disabled={isLoading}
                    className="bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 shadow-lg shadow-primary/20 mt-2"
                  >
                    {isLoading ? (
                      <span className="flex items-center space-x-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Creating account…</span>
                      </span>
                    ) : (
                      <span className="flex items-center space-x-2">
                        <span>Create Account & Join Team</span>
                        <Icon name="ArrowRight" size={16} />
                      </span>
                    )}
                  </Button>
                </form>

                <p className="text-center text-xs text-muted-foreground mt-4">
                  Already have an account?{' '}
                  <a href="/login" className="text-primary hover:underline">Sign in</a>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AcceptInvitePage;
